<template>
  <q-layout view="hHh lpR fFf">
    <AppHeader />

    <q-page-container>
      <q-page padding>
        <div
          v-if="activeBanner"
          class="runtime-banner"
          :class="`runtime-banner--${activeBanner.kind}`"
          role="status"
          aria-live="polite"
        >
          <div class="runtime-banner__icon">
            <q-icon :name="activeBanner.icon" />
          </div>
          <div class="runtime-banner__content">
            <strong>{{ activeBanner.title }}</strong>
            <span>{{ activeBanner.help }}</span>
          </div>
          <q-badge
            :color="activeBanner.badgeColor"
            :text-color="activeBanner.badgeTextColor"
            :label="activeBanner.badge"
          />
        </div>
        <NuxtPage />
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script setup lang="ts">
const dryRun = useState<boolean>('dryRun', () => false)
const simulationMode = useState<boolean>('simulationMode', () => false)
const backendOnline = useState<boolean>('backendOnline', () => true)
const { t } = useI18n()

const activeBanner = computed(() => {
  if (!backendOnline.value) {
    return {
      kind: 'offline',
      icon: 'cloud_off',
      title: t('backendOffline.title'),
      help: t('backendOffline.help'),
      badge: t('backendOffline.badge'),
      badgeColor: 'negative',
      badgeTextColor: 'white'
    }
  }
  if (simulationMode.value) {
    if (dryRun.value) {
      return {
        kind: 'simulation',
        icon: 'science',
        title: t('simulationMode.dryRunTitle'),
        help: t('simulationMode.dryRunHelp'),
        badge: t('simulationMode.dryRunBadge'),
        badgeColor: 'warning',
        badgeTextColor: 'black'
      }
    }
    return {
      kind: 'simulation',
      icon: 'science',
      title: t('simulationMode.title'),
      help: t('simulationMode.help'),
      badge: t('simulationMode.badge'),
      badgeColor: 'warning',
      badgeTextColor: 'black'
    }
  }
  if (dryRun.value) {
    return {
      kind: 'dry-run',
      icon: 'power_off',
      title: t('dryRun.activeTitle'),
      help: t('dryRun.activeHelp'),
      badge: t('dryRun.badge'),
      badgeColor: 'warning',
      badgeTextColor: 'black'
    }
  }
  return null
})
</script>

<style scoped>
.runtime-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 18px;
  padding: 12px 16px;
  color: #fff3cd;
  border: 1px solid rgba(255, 193, 7, 0.45);
  border-radius: 10px;
  background: #38352b;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
}

.runtime-banner__icon {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  flex: 0 0 38px;
  border-radius: 9px;
  color: #ffc107;
  background: rgba(255, 193, 7, 0.14);
  font-size: 22px;
}

.runtime-banner__content {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
}

.runtime-banner__content span {
  margin-top: 2px;
  color: #d5cfbd;
  font-size: 13px;
}

.runtime-banner--offline {
  color: #ffcdd2;
  border-color: rgba(244, 67, 54, 0.48);
  background: #3a2c2d;
}

.runtime-banner--offline .runtime-banner__icon {
  color: #ef5350;
  background: rgba(244, 67, 54, 0.14);
}

.runtime-banner--offline .runtime-banner__content span {
  color: #d8bfc1;
}

@media (max-width: 650px) {
  .runtime-banner {
    align-items: flex-start;
    padding: 10px 12px;
  }

  .runtime-banner .q-badge {
    display: none;
  }
}
</style>
